/**
 * Where "onboarding already seen" lives, per audience:
 *  - ``demo`` → client-side (the tour store's ``seenBy["demo"]``, localStorage):
 *    demo visitors are ephemeral, no server identity to persist against.
 *  - ``user`` → the BE user preference ``metadata.onboarding_seen`` (durable,
 *    cross-device), read via the profile query and written via the shared
 *    preferences mutation. ``ready`` gates the autostart so a returning user
 *    isn't briefly re-onboarded before the profile resolves.
 */
import { useCallback } from "react";

import { useQueryClient } from "@tanstack/react-query";
import type { UserProfileResponse } from "@ando/core/api";

import { settingsQueryKeys, useProfile, useUpdatePreferencesMutation } from "@/features/settings";
import { readCachedSession } from "@/features/auth/session";

import { useDemoTourStore } from "./demoTourStore";
import { resolveOnboardingMode } from "./useOnboardingMode";

export function useOnboardingPersistence(): {
  seen: boolean;
  ready: boolean;
  markSeen: () => void;
} {
  const isUser = resolveOnboardingMode() === "user";
  const userId = readCachedSession()?.user_id ?? null;
  const profile = useProfile(isUser ? userId : null);
  const mutation = useUpdatePreferencesMutation(isUser ? userId : null);
  const demoSeen = useDemoTourStore((s) => Boolean(s.seenBy.demo));
  const qc = useQueryClient();

  const seen = isUser
    ? Boolean(profile.data?.preferences?.metadata?.onboarding_seen)
    : demoSeen;
  // For real users wait for the profile before deciding; demo is instant.
  const ready = isUser ? profile.isSuccess : true;

  const markSeen = useCallback(() => {
    if (!isUser || !userId) return; // demo: persisted by the store's finish()
    // Flip the cached profile synchronously so the autostart gate sees "seen"
    // immediately — prevents a re-onboarding flash while the PATCH→GET round
    // trip is in flight. The mutation reconciles (and re-invalidates) after.
    qc.setQueryData<UserProfileResponse>(settingsQueryKeys.profile(userId), (old) =>
      old
        ? {
            ...old,
            preferences: {
              ...old.preferences,
              metadata: { ...(old.preferences.metadata ?? {}), onboarding_seen: true },
            },
          }
        : old,
    );
    mutation.mutate({ onboarding_seen: true });
  }, [isUser, userId, mutation, qc]);

  return { seen, ready, markSeen };
}
